import {
  Box,
  Checkbox,
  CheckboxGroup,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Select,
  Stack,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  useColorModeValue,
  useDisclosure,
  useOutsideClick,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { compact } from "lodash";
import { useRef } from "react";
import { FiFilter } from "react-icons/fi";

const typeOptions = [
  { label: "All event types", value: "" },
  { label: "Admitted", value: "Admitted" },
  { label: "Discharged", value: "Discharged" },
] as const;

const filterOptions = [
  { label: "All days", value: "" },
  { label: "2 Days", value: "2" },
  { label: "1 Week", value: "7" },
  { label: "2 Weeks", value: "14" },
  { label: "4 Weeks", value: "28" },
] as const;

const codingOptions = [
  { label: "All visit reasons", value: "" },
  { label: "Cardiac", value: "cardiac" },
] as const;

const statusOptions = [
  { label: "All statuses", value: "" },
  { label: "Not Started", value: "Not Started" },
  { label: "Attempted", value: "Attempted" },
  { label: "Completed", value: "Completed" },
] as const;

const classOptions = [
  { label: "Inpatient encounter", value: "inpatient encounter" },
  { label: "Ambulatory", value: "ambulatory" },
  { label: "Emergency", value: "emergency" },
  { label: "Pre-admission", value: "pre-admission" },
  { label: "Short stay", value: "short stay" },
] as const;

// single select option
export type SearchOption = string;
export type FacilityOption = string;
export type TypeOption = (typeof typeOptions)[number]["value"];
export type DaysOption = (typeof filterOptions)[number]["value"];
export type CodingOption = (typeof codingOptions)[number]["value"];
export type StatusOption = (typeof statusOptions)[number]["value"];
// multi select option
export type ClassOption = Array<(typeof classOptions)[number]["value"]>;

export const typeDefaultOption = typeOptions[0].value;
export const filterDefaultOption = filterOptions[0].value;
export const codingDefaultOption = codingOptions[0].value;
export const statusDefaultOption = statusOptions[0].value;
export const classDefaultOption: ClassOption = [];

export type FilterOption =
  | FacilityOption
  | TypeOption
  | DaysOption
  | CodingOption
  | StatusOption
  | SearchOption;

export type GetTcmEncounterQueryParams = {
  facility?: string;
  type?: TypeOption;
  days?: DaysOption;
  coding?: CodingOption;
  status?: StatusOption;
  class?: ClassOption;
  search?: SearchOption;
  admitTimeSort?: "asc" | "desc" | undefined;
  dischargeTimeSort?: "asc" | "desc" | undefined;
  lastOutreachDateSort?: "asc" | "desc" | undefined;
  patientFirstNameSort?: "asc" | "desc" | undefined;
  patientLastNameSort?: "asc" | "desc" | undefined;
};

interface FilterBarProps {
  facilitiesQuery: { isLoading: boolean; data?: { id: string; name: string }[] };
  selectedFacility: string | undefined;
  selectedType: TypeOption;
  selectedDays: DaysOption;
  selectedCoding: CodingOption;
  selectedStatus: StatusOption;
  selectedClass: ClassOption;
  handleFacilitySelect: (id: FacilityOption | undefined) => void;
  handleTypeSelect: (value: TypeOption) => void;
  handleDaySelect: (value: DaysOption) => void;
  handleCodingSelect: (value: CodingOption) => void;
  handleStatusSelect: (value: StatusOption) => void;
  handleClassSelect: (value: ClassOption) => void;
}

export default function FilterBar({
  facilitiesQuery,
  selectedCoding,
  selectedDays,
  selectedFacility,
  selectedStatus,
  selectedClass,
  selectedType,
  handleCodingSelect,
  handleDaySelect,
  handleFacilitySelect,
  handleStatusSelect,
  handleTypeSelect,
  handleClassSelect,
}: FilterBarProps) {
  const { isOpen, onToggle, onClose } = useDisclosure();
  const popoverRef = useRef(null);
  useOutsideClick({
    ref: popoverRef,
    handler: onClose,
  });

  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const hoverBg = useColorModeValue("gray.50", "gray.700");

  const labelFor = (
    opts: readonly { value: FilterOption; label: string }[],
    v?: FilterOption
  ): string => {
    return opts.find(o => o.value === v)?.label ?? v ?? "";
  };

  const activeFilters = compact([
    selectedFacility !== undefined &&
      selectedFacility !== "" && {
        key: "facility",
        label: facilitiesQuery.data?.find(f => f.id === selectedFacility)?.name ?? "Facility",
        onClear: () => handleFacilitySelect(undefined),
      },
    selectedType !== typeDefaultOption && {
      key: "type",
      label: labelFor(typeOptions, selectedType),
      onClear: () => handleTypeSelect(typeDefaultOption),
    },
    selectedDays !== filterDefaultOption && {
      key: "days",
      label: labelFor(filterOptions, selectedDays),
      onClear: () => handleDaySelect(filterDefaultOption),
    },
    selectedCoding !== codingDefaultOption && {
      key: "coding",
      label: labelFor(codingOptions, selectedCoding),
      onClear: () => handleCodingSelect(codingDefaultOption),
    },
    selectedStatus !== statusDefaultOption && {
      key: "status",
      label: labelFor(statusOptions, selectedStatus),
      onClear: () => handleStatusSelect(statusDefaultOption),
    },
    ...(selectedClass.length > 0
      ? selectedClass.map((cls, idx) => ({
          key: `class-${idx}`,
          label: labelFor(classOptions, cls),
          onClear: () => handleClassSelect(selectedClass.filter(c => c !== cls)),
        }))
      : []),
  ]);

  const Controls = () => {
    const common = {
      bg,
      w: "100%",
      css: { textOverflow: "ellipsis" },
      _placeholder: { color: "gray.400" },
    } as const;

    const DropdownOptions = ({
      options,
    }: {
      options: readonly { value: FilterOption; label: string }[];
    }) => {
      return (
        <>
          {options.map(o => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </>
      );
    };

    return (
      <Flex direction="column" gap={4} flexWrap="wrap" whiteSpace="normal">
        <Select
          {...common}
          placeholder={facilitiesQuery.isLoading ? "Loading..." : "All facilities"}
          isDisabled={facilitiesQuery.isLoading}
          value={selectedFacility ?? ""}
          onChange={e => handleFacilitySelect(e.target.value)}
        >
          <DropdownOptions
            options={facilitiesQuery.data?.map(f => ({ value: f.id, label: f.name })) ?? []}
          />
        </Select>

        <Select
          {...common}
          value={selectedType ?? typeOptions[0].value}
          onChange={e => handleTypeSelect(e.target.value as typeof selectedType)}
        >
          <DropdownOptions options={typeOptions} />
        </Select>

        <Select
          {...common}
          value={selectedDays ?? filterOptions[0].value}
          onChange={e => handleDaySelect(e.target.value as typeof selectedDays)}
        >
          <DropdownOptions options={filterOptions} />
        </Select>

        <Select
          {...common}
          value={selectedCoding ?? codingOptions[0].value}
          onChange={e => handleCodingSelect(e.target.value as typeof selectedCoding)}
        >
          <DropdownOptions options={codingOptions} />
        </Select>
        <Select
          {...common}
          value={selectedStatus ?? statusOptions[0].value}
          onChange={e => handleStatusSelect(e.target.value as typeof selectedStatus)}
        >
          <DropdownOptions options={statusOptions} />
        </Select>

        <Menu closeOnSelect={false}>
          <MenuButton
            as={Box}
            px={4}
            py={2}
            borderWidth="1px"
            borderRadius="md"
            borderColor="inherit"
            bg={bg}
            cursor="pointer"
            w="100%"
            textAlign="left"
            _hover={{ borderColor: "gray.400" }}
          >
            <Flex justify="space-between" align="center">
              <Text color={selectedClass.length === 0 ? "gray.400" : "inherit"}>
                {selectedClass.length === 0
                  ? "All classes"
                  : `${selectedClass.length} class${selectedClass.length > 1 ? "es" : ""} selected`}
              </Text>
              <ChevronDownIcon />
            </Flex>
          </MenuButton>
          <MenuList minW="200px" p={3}>
            <CheckboxGroup
              value={selectedClass}
              onChange={values => handleClassSelect(values as ClassOption)}
            >
              <Stack spacing={2}>
                {classOptions.map(opt => (
                  <Checkbox key={opt.value} value={opt.value}>
                    {opt.label}
                  </Checkbox>
                ))}
              </Stack>
            </CheckboxGroup>
          </MenuList>
        </Menu>
      </Flex>
    );
  };

  return (
    <Flex w="100%" align="center" gap={2}>
      <Flex
        flex="1"
        minW={0}
        gap={2}
        overflowX="auto"
        flexWrap="nowrap"
        whiteSpace="nowrap"
        sx={{
          "::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {activeFilters.map(f => (
          <Tag key={f.key} size="sm" colorScheme="orange" variant="subtle" borderRadius="md">
            <TagLabel>{f.label}</TagLabel>
            <TagCloseButton onClick={f.onClear} />
          </Tag>
        ))}
      </Flex>
      <Box ref={popoverRef}>
        <Popover placement="bottom-start" isOpen={isOpen} onClose={onClose}>
          <PopoverTrigger>
            <Flex
              as="button"
              align="center"
              gap={2}
              px={3}
              py={1}
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="md"
              bg={bg}
              whiteSpace="nowrap"
              _hover={{ bg: hoverBg }}
              onClick={onToggle}
            >
              <FiFilter />
              <span>Edit Filters</span>
            </Flex>
          </PopoverTrigger>
          <PopoverContent w="auto" p={4} bg={bg} borderColor={borderColor}>
            <PopoverBody>
              <Controls />
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </Box>
    </Flex>
  );
}
